import React, { useCallback } from "react";
import { Box, Stack, TextField, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import SearchBar from "./SearchBar";
import FilterDropdown from "./FilterDropdown";
import SearchButton from "./SearchButton";

export default function SearchFilterBar(props) {
  //#region Props
  const {
    // -------------------------------------------------------------------------
    // Behavior
    // -------------------------------------------------------------------------
    mode = "backend", // "backend" | "frontend"

    // -------------------------------------------------------------------------
    // Container / Layout
    // -------------------------------------------------------------------------
    containerSx = {},
    stackSx = {},
    spacing = 2,

    // -------------------------------------------------------------------------
    // Search
    // -------------------------------------------------------------------------
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "Search",
    searchBarProps = {},
    searchBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Filter Dropdown (existing)
    // -------------------------------------------------------------------------
    showFilterButton = true,
    filterValue,
    onFilterChange,
    filterOptions = [],
    filterLabel = "Filter",
    filterPlaceholder = "All",
    filterProps = {},
    filterBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Search Button (existing)
    // -------------------------------------------------------------------------
    showSearchButton = true,
    onSearch,
    searchButtonText = "Search",
    searchButtonProps = {},
    searchButtonBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Date Range
    // -------------------------------------------------------------------------
    showDateRange = false,
    startDateLabel = "Start date",
    endDateLabel = "End date",
    startDateValue = "",
    endDateValue = "",
    onStartDateChange,
    onEndDateChange,
    dateInputSx = {},
    dateBoxSx = {},

    // -------------------------------------------------------------------------
    // Optional Actions (Apply / Clear)
    // -------------------------------------------------------------------------
    showActions = false,
    applyText = "Apply",
    clearText = "Clear",
    onApply,
    onClear,
    applyButtonProps = {},
    clearButtonProps = {},
    actionsBoxSx = {},
    layout = " responsive", // "row" | "column" | "compact" | "responsive"
  } = props;
  //#endregion

  //#region Handlers

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;

      if (mode === "backend") {
        if (showActions && typeof onApply === "function") return onApply();
        if (typeof onSearch === "function") return onSearch();
        return;
      }

      // frontend mode: optional; if callbacks exist, run them
      if (showActions && typeof onApply === "function") return onApply();
      if (typeof onSearch === "function") return onSearch();
    },
    [mode, showActions, onApply, onSearch]
  );
  //#endregion

  //#region Render Helpers
  const renderSearch = () => (
    <Box sx={{ minWidth: 0, ...searchBoxSx }}>
      <SearchBar
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        onKeyDown={handleKeyDown}
        fullWidth
        {...searchBarProps}
      />
    </Box>
  );

  const renderFilter = () => {
    if (!showFilterButton) return null;

    return (
      <Box sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 200 }, ...filterBoxSx }}>
        <FilterDropdown
          value={filterValue}
          onChange={onFilterChange}
          options={filterOptions}
          label={filterLabel}
          placeholder={filterPlaceholder}
          fullWidth
          {...filterProps}
        />
      </Box>
    );
  };

  const renderDateRange = () => {
    if (!showDateRange) return null;

    return (
      <>
        <TextField
          label={startDateLabel}
          type="date"
          size="small"
          value={startDateValue}
          onChange={(e) => onStartDateChange?.(e.target.value)}
          InputLabelProps={{ shrink: true }}
          onKeyDown={handleKeyDown}
          fullWidth
          sx={dateInputSx}
        />

        <TextField
          label={endDateLabel}
          type="date"
          size="small"
          value={endDateValue}
          onChange={(e) => onEndDateChange?.(e.target.value)}
          InputLabelProps={{ shrink: true }}
          onKeyDown={handleKeyDown}
          fullWidth
          sx={dateInputSx}
        />
      </>
    );
  };

  const renderSearchButton = () => {
    if (!showSearchButton) return null;

    return (
      <Box sx={{ width: { xs: "100%", md: "auto" }, ...searchButtonBoxSx }}>
        <SearchButton 
          onClick={onSearch} 
          text={searchButtonText} 
          fullWidth 
          {...searchButtonProps} 
        />
      </Box>
    );
  };

  const renderActions = () => {
    if (!showActions) return null;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
          gap: 1,
          ...actionsBoxSx,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={onApply}
          startIcon={<SearchIcon fontSize="small" />}
          sx={{ flex: 1.5 }}
          {...applyButtonProps}
        >
          {applyText}
        </Button>

        <Button
          variant="text"
          color="secondary"
          onClick={onClear}
          startIcon={<ClearIcon fontSize="small" />}
          sx={{ flex: 1 }}
          {...clearButtonProps}
        >
          {clearText}
        </Button>
      </Box>
    );
  };
  //#endregion

  //#region layoutSx
  const getLayoutSx = () => {
    switch (layout) {
      case "row":
        return {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
        };

      case "column":
        return {
          display: "flex",
          flexDirection: "column",
        };

      case "compact":
        return {
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "2fr 1fr 1fr auto",
          },
        };

      case "responsive":
      default:
        return {
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "2fr 1fr 1fr 1fr auto",
          },
        };
    }
  };
  // #endregion

  //#region Output
  return (
   <Box
      sx={{
        ...getLayoutSx(),
        gap: spacing,
        width: "100%",
        ...containerSx,
      }}
    >
      {renderSearch()}
      {renderFilter()}
      {renderDateRange()}
      {renderSearchButton()}
      {renderActions()}
    </Box>
  );
  //#endregion

}
